import {
  ROTULO_TIPO_TECNICO,
  TIPOS_TECNICO,
  type NovoTecnico,
  type TecnicoForm,
  type TecnicoResumo,
  type TipoTecnico,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, Star, Upload } from 'lucide-react';
import { useState } from 'react';
import { ImportarPlanilha } from '../gestao/ImportarPlanilha';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';

const VAZIO: NovoTecnico = { nome: '', email: '', senha: '', tipo: 'campo', ativo: true };

/**
 * De que time o técnico é. Decide a fila em que ele cai: campo vai para a Liberação, rede
 * vai para o NOC — e as palavras-chave de cada um são outras.
 */
function EscolherTipo({
  valor,
  onChange,
}: {
  valor: TipoTecnico;
  onChange: (tipo: TipoTecnico) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-medium">Time</span>
      <div className="mt-1 flex gap-2">
        {TIPOS_TECNICO.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange(t)}
            aria-pressed={valor === t}
            className={`flex-1 rounded-card border px-3 py-2 text-sm font-medium transition ${
              valor === t
                ? 'border-marca-600 bg-marca-600 text-white'
                : 'border-neutral-300 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800'
            }`}
          >
            {ROTULO_TIPO_TECNICO[t]}
          </button>
        ))}
      </div>
      <p className="mt-1 text-xs text-neutral-500">
        {valor === 'campo'
          ? 'Instala e repara na casa do cliente. Cai na fila da Liberação.'
          : 'Cuida da rede e dos equipamentos. Cai na fila do NOC.'}
      </p>
    </div>
  );
}

/**
 * Técnicos de campo que pedem liberação de sinal.
 *
 * Não são clientes da Maxx nem gente da equipe: entram pelo chat, no mesmo campo onde o
 * cliente digita o CPF, usando o e-mail cadastrado aqui. A senha é entregue pelo admin e
 * o técnico é obrigado a trocar no primeiro acesso.
 */
export function Tecnicos() {
  const qc = useQueryClient();
  const lista = useQuery({ queryKey: ['tecnicos'], queryFn: gestaoApi.listarTecnicos });
  const [importando, setImportando] = useState(false);
  const [novo, setNovo] = useState<NovoTecnico | null>(null);
  const [editando, setEditando] = useState<{ id: string; form: TecnicoForm } | null>(null);
  const [senhaDe, setSenhaDe] = useState<{ id: string; nome: string; senha: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['tecnicos'] });
    setNovo(null);
    setEditando(null);
    setSenhaDe(null);
    setErro(null);
  };
  const tratarErro = (e: unknown) =>
    setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar');

  const criar = useMutation({
    mutationFn: (form: NovoTecnico) => gestaoApi.criarTecnico(form),
    onSuccess: recarregar,
    onError: tratarErro,
  });
  const atualizar = useMutation({
    mutationFn: ({ id, form }: { id: string; form: TecnicoForm }) =>
      gestaoApi.atualizarTecnico(id, form),
    onSuccess: recarregar,
    onError: tratarErro,
  });
  const trocarSenha = useMutation({
    mutationFn: ({ id, senha }: { id: string; senha: string }) =>
      gestaoApi.trocarSenhaTecnico(id, senha),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  const tecnicos = lista.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-neutral-500">
          Quem pede liberação de sinal em campo. Entram pelo chat com o e-mail cadastrado aqui, e
          precisam fazer login uma vez por dia.
        </p>
        <div className="flex gap-2">
          <Botao variante="secundario" onClick={() => setImportando(true)}>
            <Upload size={16} /> Importar planilha
          </Botao>
          <Botao
            onClick={() => {
              setNovo({ ...VAZIO });
              setErro(null);
            }}
          >
            <Plus size={16} /> Novo técnico
          </Botao>
        </div>
      </div>

      {importando && (
        <ImportarPlanilha
          tipo="tecnicos"
          aoFechar={() => setImportando(false)}
          aoImportar={() => void qc.invalidateQueries({ queryKey: ['tecnicos'] })}
        />
      )}

      {erro && !novo && !editando && !senhaDe && <Aviso tipo="erro">{erro}</Aviso>}

      {tecnicos.length === 0 && !lista.isPending && (
        <div className="rounded-card border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
          Nenhum técnico cadastrado ainda.
        </div>
      )}

      <div className="space-y-2">
        {tecnicos.map((t) => (
          <Linha
            key={t.id}
            tecnico={t}
            onEditar={() => {
              setEditando({
                id: t.id,
                form: { nome: t.nome, email: t.email, tipo: t.tipo, ativo: t.ativo },
              });
              setErro(null);
            }}
            onSenha={() => {
              setSenhaDe({ id: t.id, nome: t.nome, senha: '' });
              setErro(null);
            }}
          />
        ))}
      </div>

      <Modal
        aberto={!!novo}
        titulo="Novo técnico"
        onFechar={() => setNovo(null)}
        acoes={
          <Botao carregando={criar.isPending} onClick={() => novo && criar.mutate(novo)}>
            Cadastrar
          </Botao>
        }
      >
        {novo && (
          <div className="space-y-3">
            <CampoTexto
              rotulo="Nome completo"
              autoFocus
              value={novo.nome}
              onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
            />
            <CampoTexto
              rotulo="E-mail"
              type="email"
              value={novo.email}
              onChange={(e) => setNovo({ ...novo, email: e.target.value })}
              ajuda="É o que ele digita no chat, no lugar do CPF."
            />
            <EscolherTipo valor={novo.tipo} onChange={(tipo) => setNovo({ ...novo, tipo })} />
            <CampoTexto
              rotulo="Senha inicial"
              type="password"
              value={novo.senha}
              onChange={(e) => setNovo({ ...novo, senha: e.target.value })}
              ajuda="Entregue a ele. O sistema obriga a trocar no primeiro acesso."
            />
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>

      <Modal
        aberto={!!editando}
        titulo="Editar técnico"
        onFechar={() => setEditando(null)}
        acoes={
          <Botao
            carregando={atualizar.isPending}
            onClick={() => editando && atualizar.mutate(editando)}
          >
            Salvar
          </Botao>
        }
      >
        {editando && (
          <div className="space-y-3">
            <CampoTexto
              rotulo="Nome completo"
              value={editando.form.nome}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, nome: e.target.value } })
              }
            />
            <CampoTexto
              rotulo="E-mail"
              type="email"
              value={editando.form.email}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, email: e.target.value } })
              }
            />
            <EscolherTipo
              valor={editando.form.tipo}
              onChange={(tipo) => setEditando({ ...editando, form: { ...editando.form, tipo } })}
            />
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={editando.form.ativo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, ativo: e.target.checked } })
                }
              />
              <span>
                Ativo
                <span className="block text-xs text-neutral-500">
                  Desmarcar derruba as sessões abertas dele na hora.
                </span>
              </span>
            </label>
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>

      <Modal
        aberto={!!senhaDe}
        titulo={`Nova senha de ${senhaDe?.nome ?? ''}`}
        onFechar={() => setSenhaDe(null)}
        acoes={
          <Botao
            carregando={trocarSenha.isPending}
            onClick={() => senhaDe && trocarSenha.mutate(senhaDe)}
          >
            Definir
          </Botao>
        }
      >
        {senhaDe && (
          <div className="space-y-3">
            <CampoTexto
              rotulo="Senha"
              type="password"
              autoFocus
              value={senhaDe.senha}
              onChange={(e) => setSenhaDe({ ...senhaDe, senha: e.target.value })}
              ajuda="Ele será obrigado a trocar ao entrar, e as sessões abertas caem."
            />
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Linha({
  tecnico,
  onEditar,
  onSenha,
}: {
  tecnico: TecnicoResumo;
  onEditar: () => void;
  onSenha: () => void;
}) {
  return (
    <div className="rounded-card flex flex-wrap items-center gap-3 border border-neutral-200 px-4 py-3 dark:border-neutral-800">
      <div className="min-w-0 flex-1">
        <p
          className={`flex items-center gap-2 font-medium ${tecnico.ativo ? '' : 'text-neutral-400'}`}
        >
          {tecnico.nome}
          {/* O time fica junto do nome, e não no canto com os números: é ele que decide
              em qual fila a pessoa cai, e achar "quem é de rede" numa lista de trinta
              nomes tem que ser olhar, não ler. */}
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-normal ${
              tecnico.tipo === 'rede'
                ? 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300'
                : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
            }`}
          >
            {ROTULO_TIPO_TECNICO[tecnico.tipo]}
          </span>
          {!tecnico.ativo && <span className="text-xs">(inativo)</span>}
        </p>
        <p className="truncate text-xs text-neutral-500">{tecnico.email}</p>
      </div>

      <div className="flex shrink-0 items-center gap-4 text-xs text-neutral-500">
        {tecnico.senhaTemporaria && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
            Senha ainda não trocada
          </span>
        )}
        <span>{tecnico.totalAtendimentos} atendimentos</span>
        {tecnico.notaMedia !== null && (
          <span className="flex items-center gap-0.5">
            <Star size={11} className="fill-amber-400 text-amber-400" />
            {tecnico.notaMedia.toFixed(1)}
          </span>
        )}
        <span>
          {tecnico.ultimoLoginEm
            ? `Entrou ${new Date(tecnico.ultimoLoginEm).toLocaleDateString('pt-BR')}`
            : 'Nunca entrou'}
        </span>
      </div>

      <div className="flex shrink-0 gap-1">
        <Botao variante="secundario" onClick={onSenha} aria-label="Nova senha">
          <KeyRound size={15} />
        </Botao>
        <Botao variante="secundario" onClick={onEditar} aria-label="Editar">
          <Pencil size={15} />
        </Botao>
      </div>
    </div>
  );
}
